import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { buildCreateApi, coreModule, fetchBaseQuery, reactHooksModule } from '@reduxjs/toolkit/query/react';
import fetch from 'isomorphic-fetch';

import { SESSION as SESSION_COOKIE } from '~/constants/other';
import { API_HOST } from '~/utils/api';

const customCreateApi = buildCreateApi(coreModule(), reactHooksModule({ unstable__sideEffectsInRender: true }));

interface Extra {
  cookies?: Record<string, string>; // cookies can be undefined
}

const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_HOST,
  credentials: 'include',
  prepareHeaders: (headers, { extra }) => {
    headers.set('Content-Type', 'application/json');
    headers.set('Authorization', `Bearer ${(extra as Extra | undefined)?.cookies?.[SESSION_COOKIE]}`);
    return headers;
  },
  ...(typeof window !== 'undefined' && { fetchFn: fetch }),
});

export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  { extra?: Extra }
> = async (args, api, extraOptions) => {
  let result = await rawBaseQuery(args, api, extraOptions);

  // if 401 → try to refresh token
  if (result.error?.status === 401) {
    const refreshResult = await rawBaseQuery({ url: '/auth/refresh', method: 'POST' }, api, extraOptions);

    if (refreshResult.data) {
      result = await rawBaseQuery(args, api, extraOptions);
    } else {
      // not successful refresh → logout
      api.dispatch({ type: 'auth/logout' });
    }
  }

  return result;
};

export const api = customCreateApi({
  reducerPath: 'api',
  tagTypes: [
    'ADMIN',
    'ADMIN_INVITE',
    'PLAYER',
    'TEAM',
    'TEAM_MEMBERSHIPS',
    'TOURNAMENT',
    'AUTH_INVITE_ACTION',
    'TOUR',
    'TOURNAMENT_TEAM',
    'TOURNAMENT_PARTICIPANTS',
    'GROUP',
    'TOUR_TYPE',
    'REFEREE',
    'ASSETS',
    'GAME',
    'POSITION',
  ],
  baseQuery: baseQueryWithReauth,
  endpoints: () => ({}),
});

export default api;
